import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Menu, ArrowLeft, ArrowRight, ChevronLeft, Bookmark, BookmarkCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChapterContent from "@/components/ChapterContent";
import EngagementSection from "@/components/EngagementSection";
import { EBOOK } from "@/data/ebookData";

const getBookmarks = () => {
  try { return JSON.parse(localStorage.getItem("bookmarks") || "[]"); }
  catch { return []; }
};
const toggleBookmarkStorage = (chId) => {
  const bk = getBookmarks();
  const idx = bk.indexOf(chId);
  if (idx > -1) bk.splice(idx, 1);
  else bk.push(chId);
  localStorage.setItem("bookmarks", JSON.stringify(bk));
  return bk;
};

export default function ReaderPage() {
  const { ebookId, chapterId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bookmarks, setBookmarks] = useState(getBookmarks);

  const currentChapter = chapterId ? parseInt(chapterId) : 1;
  const chapter = EBOOK.chapters.find((c) => c.id === currentChapter);
  const totalChapters = EBOOK.chapters.length;
  const isBookmarked = bookmarks.includes(currentChapter);

  const handleToggleBookmark = () => {
    const updated = toggleBookmarkStorage(currentChapter);
    setBookmarks([...updated]);
  };

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight > 0) {
      setProgress((scrollTop / docHeight) * 100);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (chapter) {
      document.title = `Ch ${chapter.number}: ${chapter.title} | What AI Is NOT | Shadab Rashid`;
    }
  }, [currentChapter, chapter]);

  const goToChapter = (id) => {
    navigate(`/read/${ebookId || "what-ai-is-not"}/${id}`);
    setSidebarOpen(false);
  };

  const ChapterList = ({ onSelect }) => (
    <div>
      {EBOOK.chapters.map((ch) => (
        <button
          key={ch.id}
          onClick={() => onSelect(ch.id)}
          data-testid={`chapter-nav-${ch.id}`}
          className={`w-full text-left chapter-link ${
            ch.id === currentChapter ? "active" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-[#FF4F00] font-bold">
              {ch.number}
            </span>
            {bookmarks.includes(ch.id) && (
              <BookmarkCheck size={12} className="text-[#FF4F00]" />
            )}
          </div>
          <p className={`text-sm mt-0.5 leading-snug ${
            ch.id === currentChapter ? "font-semibold text-[#0A0A0A]" : "text-[#666666]"
          }`}>
            {ch.title}
          </p>
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="reader-page">
      {/* Progress bar */}
      <div className="reading-progress" data-testid="reading-progress">
        <div className="reading-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Top nav */}
      <nav className="sticky top-1 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E5E5E5]">
        <div className="flex items-center justify-between px-4 md:px-6 h-14">
          <div className="flex items-center gap-3">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button data-testid="reader-menu-toggle" className="p-1.5">
                  <Menu size={20} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-white p-0">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
                  <span className="font-cabinet font-bold text-sm">Chapters</span>
                </div>
                <ScrollArea className="h-[calc(100vh-64px)]">
                  <ChapterList onSelect={goToChapter} />
                </ScrollArea>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0A0A] transition-colors" data-testid="reader-back-home">
              <ChevronLeft size={16} />
              <span className="hidden sm:inline font-cabinet font-medium">The AI Mastery Series</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleBookmark}
              data-testid="bookmark-button"
              className={`p-1.5 transition-colors ${
                isBookmarked ? "text-[#FF4F00]" : "text-[#666666] hover:text-[#0A0A0A]"
              }`}
              title={isBookmarked ? "Remove bookmark" : "Bookmark this chapter"}
            >
              {isBookmarked ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
            </button>
            <span className="hidden md:inline font-cabinet font-medium text-xs text-[#666666]">
              Shadab Rashid
            </span>
            <span className="font-mono-plex text-xs text-[#666666]">
              Ch {String(currentChapter).padStart(2, "0")} / {totalChapters}
            </span>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block fixed left-0 top-[60px] bottom-0 w-[280px] border-r border-[#E5E5E5] bg-white z-30" data-testid="reader-sidebar">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <p className="font-cabinet font-bold text-sm text-[#0A0A0A]">What AI Is NOT</p>
            <p className="font-cabinet text-xs text-[#666666] mt-0.5">Shadab Rashid</p>
            <p className="font-mono-plex text-[10px] text-[#666666] mt-1">{totalChapters} Chapters</p>
          </div>
          <ScrollArea className="h-[calc(100vh-120px)]">
            <ChapterList onSelect={goToChapter} />
          </ScrollArea>
        </aside>

        {/* Main content */}
        <main className="flex-1 lg:ml-[280px] px-4 md:px-8 lg:px-16 py-8 lg:py-16 overflow-x-hidden">
          <div className="max-w-[65ch] mx-auto">
            <ChapterContent chapter={chapter} />

            {/* End of Book: Acknowledgment, only after last chapter */}
            {currentChapter === totalChapters && (
              <div className="mt-16" data-testid="end-of-book">
                {/* Closing divider */}
                <div className="flex items-center gap-4 mb-12">
                  <div className="flex-1 h-[1px] bg-[#E5E5E5]" />
                  <span className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#FF4F00] font-bold">End of Book</span>
                  <div className="flex-1 h-[1px] bg-[#E5E5E5]" />
                </div>

                {/* Credits block */}
                <div className="bg-[#0A0A0A] p-8 md:p-12 mb-12" data-testid="book-credits">
                  <p className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#FF4F00] mb-6">
                    The AI Mastery Series
                  </p>
                  <h2 className="font-cabinet font-bold text-3xl md:text-4xl text-white tracking-tighter leading-none">
                    What AI Is <span className="text-[#FF4F00]">NOT</span>
                  </h2>
                  <div className="w-10 h-[2px] bg-[#FF4F00] mt-6 mb-6" />
                  <div className="space-y-4">
                    <div>
                      <p className="font-mono-plex text-[9px] tracking-[0.15em] uppercase text-white/40">Thought by</p>
                      <p className="font-cabinet font-bold text-xl text-white mt-1">Shadab Rashid</p>
                    </div>
                    <div>
                      <p className="font-mono-plex text-[9px] tracking-[0.15em] uppercase text-white/40">Compiled &amp; Curated by</p>
                      <p className="font-cabinet font-bold text-xl text-white mt-1">Shifa Shaikh</p>
                    </div>
                  </div>
                  <p className="text-xs text-white/30 font-mono-plex mt-8">
                    &copy; {new Date().getFullYear()} Shadab Rashid. All rights reserved.
                  </p>
                </div>

                {/* Acknowledgment */}
                <div className="mb-12" data-testid="acknowledgment-section">
                  <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#FF4F00] font-bold">
                    Acknowledgment
                  </span>
                  <h2 className="font-cabinet font-bold text-2xl md:text-3xl tracking-tight mt-3 text-[#0A0A0A]">
                    A Note of Gratitude
                  </h2>
                  <div className="w-10 h-[2px] bg-[#FF4F00] mt-4 mb-8" />

                  <div className="space-y-6 font-plex text-base leading-loose text-[#0A0A0A]">
                    <p>
                      This book exists because of <strong>Shifa Shaikh</strong>. She took scattered ideas and turned them into something clear, structured, and worth reading. She challenged what needed challenging, refined what needed refining, and held the standard when it mattered most.
                    </p>
                    <p>
                      Her work shaped this book and will shape every book that follows in The AI Mastery Series.
                    </p>
                  </div>

                  <div className="mt-8 border-l-2 border-[#FF4F00] pl-6">
                    <p className="font-cabinet font-medium text-lg md:text-xl italic text-[#0A0A0A] leading-relaxed">
                      "Thank you, Shifa. This series is better because of you."
                    </p>
                    <p className="font-cabinet font-bold text-sm text-[#0A0A0A] mt-4">Shadab Rashid</p>
                  </div>
                </div>
              </div>
            )}

            {/* Engagement: Likes + Comments */}
            <EngagementSection
              ebookId={ebookId || "what-ai-is-not"}
              chapterId={currentChapter}
            />

            {/* Chapter navigation */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-[#E5E5E5]">
              {currentChapter > 1 ? (
                <button
                  onClick={() => goToChapter(currentChapter - 1)}
                  data-testid="prev-chapter-button"
                  className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0A0A] transition-colors"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden sm:inline">
                    {EBOOK.chapters[currentChapter - 2]?.title}
                  </span>
                  <span className="sm:hidden">Previous</span>
                </button>
              ) : (
                <div />
              )}
              {currentChapter < totalChapters ? (
                <button
                  onClick={() => goToChapter(currentChapter + 1)}
                  data-testid="next-chapter-button"
                  className="flex items-center gap-2 text-sm font-medium text-[#FF4F00] hover:text-[#E64700] transition-colors"
                >
                  <span className="hidden sm:inline">
                    {EBOOK.chapters[currentChapter]?.title}
                  </span>
                  <span className="sm:hidden">Next Chapter</span>
                  <ArrowRight size={16} />
                </button>
              ) : (
                <Link
                  to="/"
                  data-testid="finish-reading-link"
                  className="flex items-center gap-2 text-sm font-medium text-[#FF4F00]"
                >
                  Back to Home <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
